import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { QUOTE_URGENCY } from 'src/constants/common';
import { PATH } from 'src/constants/path';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { RootState } from 'src/redux/store';
import { getQuote } from 'src/services/QuoteService';
import WalToast from 'src/utils/WalToast';

import { consigneeFormKeys, shipperFormKeys } from '../Modals/quoteDetailModal.const';

import { ADD_QUOTE_KEYS, ADD_QUOTE_STEPS } from './addQuote.const';
import { resetAddQuoteData, setAddQuoteCompletedStep, setAddQuoteData, setAddQuoteStep } from './AddQuote.slice';
import { buildDuplicateMultiStopState, stopStepKey } from './multiStop.helpers';

/**
 * useDuplicateQuote — shared "Duplicate Quote" action for the customer portal (quote list row +
 * quote detail "Quote Actions" dropdown). Fetches the source quote, pre-fills the Add Quote wizard
 * (classic 2-stop AND multi-stop), marks every step complete and lands on the last step.
 *
 * Client decision — customers can no longer EDIT a quote once created; duplicating into a fresh
 * quote is the supported path, so both entry points must behave identically (hence one hook).
 */
const useDuplicateQuote = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const currentUser = useAppSelector((state: RootState) => state.auth.currentUser);
    // id of the quote currently being duplicated — lets callers show a per-row/per-button spinner.
    const [duplicatingQuoteId, setDuplicatingQuoteId] = useState<number | null>(null);

    const duplicateQuote = useCallback(
        async (quote: any) => {
            try {
                setDuplicatingQuoteId(quote.id);

                const response = await getQuote(quote.id);

                if (response?.data && response?.status) {
                    const { dimensions: rawDimensions, selectedServices, ...rest } = response.data;
                    // Multi-stop duplicate — getQuoteById returns the customer-visible `stops` plus
                    // per-dimension routing; convert them into the wizard's stopCount/stops state (null
                    // for classic quotes, which keep the 2-stop path untouched below).
                    const multiStop = buildDuplicateMultiStopState(rest.stops, rawDimensions);
                    const dimensions = multiStop ? multiStop.dimensions : rawDimensions;
                    const shipperAddress = Object.entries(shipperFormKeys).reduce((acc, [apiKey, addressKey]) => {
                        acc[addressKey] = rest[apiKey] ?? null;

                        return acc;
                    }, {} as any);

                    const consigneeAddress = Object.entries(consigneeFormKeys).reduce((acc, [apiKey, addressKey]) => {
                        acc[addressKey] = rest[apiKey] ?? null;

                        return acc;
                    }, {} as any);

                    const singleDivision =
                        currentUser?.divisions?.length === 1 ? currentUser.divisions[0] : currentUser?.divisions?.find((d: any) => d.id === rest.divisionId) || currentUser?.divisions?.[0];
                    const billingAccounts = singleDivision?.customerBillingAccount || [];
                    const resolvedDivisionId = singleDivision?.id ?? rest.divisionId ?? null;
                    const resolvedBillingAccountId =
                        billingAccounts.length === 1
                            ? billingAccounts[0]?.id
                            : billingAccounts.find((b: any) => b.id === rest.customerBillingAccountId)?.id ?? billingAccounts[0]?.id ?? rest.customerBillingAccountId ?? null;

                    // selectedServices is an array: [{ serviceId, service: { id, type, slug }, ... }]
                    const services: any[] = Array.isArray(selectedServices) ? selectedServices : [];

                    const shipperServiceIds = services.filter((s: any) => s.service?.type === 'PICKUP').map((s: any) => s.serviceId);
                    const consigneeServiceIds = services.filter((s: any) => s.service?.type === 'DELIVERY').map((s: any) => s.serviceId);
                    const otherServiceIds = services.filter((s: any) => s.service?.type === 'OTHER').map((s: any) => s.serviceId);

                    const limitedAccessService = services.find((s: any) => s.service?.slug === 'limited_access_delivery');
                    const inbondService = services.find((s: any) => s.service?.slug === 'in-bond' || s.service?.slug === 'inbond');
                    const dangerousGoodsService = services.find((s: any) => s.service?.slug === 'dangerous_goods');

                    dispatch(resetAddQuoteData({ clearAll: true }));
                    dispatch(
                        setAddQuoteData({
                            data: {
                                // Carried through the wizard and sent on create so the BE can write the
                                // "duplicated quote <code>" activity (hyperlinked back to this quote) on
                                // the new quote's feed. Deliberately NOT in resetAddQuoteData's
                                // soft-reset carry-over list (serviceType/division/billing/urgency/
                                // stopCount), so an abandoned duplicate can't leak the source id into
                                // the next quote the customer starts from scratch.
                                duplicatedFromQuoteId: quote.id,
                                // stopCount drives which stepper renders (NewAddQuote: stopCount > 2 →
                                // multi-stop steps); 2 keeps the classic shipper→consignee flow.
                                stopCount: multiStop?.stopCount ?? 2,
                                stops: multiStop?.stops ?? null,
                                divisionId: resolvedDivisionId,
                                customerBillingAccountId: resolvedBillingAccountId,
                                serviceType: rest.classification,
                                urgency: QUOTE_URGENCY.LIVE,
                                serviceLevel: rest.serviceLevel ?? 'standard',
                                shipperDetails: {
                                    shipperId: rest.shipperId,
                                    shipperCompanyName: rest.shipperCompanyName,
                                    shipperAddress,
                                    pickupSensitivity: rest.pickupSensitivity,
                                    pickupDate: rest.pickupDate,
                                    pickupEndDate: rest?.pickupEndDate,
                                    pickupWindowType: rest.pickupWindowType || null,
                                    pickupStartTime: rest?.pickupStartTime,
                                    pickupEndTime: rest?.pickupEndTime,
                                    serviceId: shipperServiceIds,
                                },
                                consigneeDetails: {
                                    consigneeId: rest.consigneeId,
                                    consigneeCompanyName: rest.consigneeCompanyName,
                                    consigneeAddress,
                                    deliverySensitivity: rest.deliverySensitivity,
                                    deliveryDate: rest.deliveryDate,
                                    deliveryEndDate: rest?.deliveryEndDate,
                                    deliveryWindowType: rest.deliveryWindowType || null,
                                    deliveryStartTime: rest?.deliveryStartTime,
                                    deliveryEndTime: rest?.deliveryEndTime,
                                    serviceId: consigneeServiceIds,
                                    limitedAccessDelivery: !!limitedAccessService,
                                    limitedAccessDeliveryType: limitedAccessService?.limitedAccessType || '',
                                    limitedAccessDeliveryOther: limitedAccessService?.limitedAccessOther || '',
                                },
                                dimensionsDetails: {
                                    dimensions: dimensions || [],
                                    weightMeasurement: dimensions?.[0]?.weightMeasurement || 'lbs',
                                    lengthMeasurement: dimensions?.[0]?.lengthMeasurement || 'in',
                                    linearFootage: rest.linearFootage,
                                    totalWeight: rest.totalWeight,
                                    serviceId: otherServiceIds,
                                    additionalServices: {
                                        serviceId: otherServiceIds,
                                        inbond: !!inbondService,
                                        inbondType: inbondService?.inbondType || null,
                                        inbondName: inbondService?.inbondName || null,
                                        inbondAddress: inbondService?.inbondAddress || null,
                                        inbondContactMethod: inbondService?.inbondContactMethod || null,
                                        inbondContactValue: inbondService?.inbondContactValue || null,
                                        dangerousGoods: !!dangerousGoodsService,
                                        dangerGoodsType: dangerousGoodsService?.dangerGoodsType || null,
                                        unitedNationsNumber: dangerousGoodsService?.unitedNationsNumber || null,
                                        packagingGroup: dangerousGoodsService?.packagingGroup || null,
                                        goodsClass: dangerousGoodsService?.goodsClass || null,
                                        description: dangerousGoodsService?.description || null,
                                        emergencyContactName: dangerousGoodsService?.emergencyContactName || null,
                                        emergencyContactNumber: dangerousGoodsService?.emergencyContactNumber || null,
                                        emergencyContactExt: dangerousGoodsService?.emergencyContactExt || null,
                                        emergencyEapnbr: dangerousGoodsService?.emergencyEapnbr || null,
                                    },
                                },
                                shipmentDetails: {
                                    primaryEquipmentType: rest.primaryEquipmentType,
                                    primaryEquipmentTypeFullName: rest.primaryEquipmentTypeFullName,
                                    secondaryEquipmentTypes: rest.secondaryEquipmentTypes,
                                    secondaryEquipmentTypeFullNames: rest.secondaryEquipmentTypeFullNames,
                                    isLoadInsuranceEnabled: rest.isLoadInsuranceEnabled,
                                    insuranceCommodity: rest.insuranceCommodity,
                                    insuranceCommercialValue: rest.insuranceCommercialValue,
                                    insuranceCurrencyType: rest.insuranceCurrencyType,
                                    insuranceCommodityId: rest.insuranceCommodityId,
                                    specialNotes: rest.specialNotes || null,
                                },
                            },
                        })
                    );

                    // Middle-stop steps ('stop_details_2'..) are also pre-filled, so mark them complete
                    // alongside the fixed steps (empty for classic quotes).
                    const middleStopStepKeys = multiStop ? Array.from({ length: multiStop.stopCount - 2 }, (_, i) => stopStepKey(i + 2)) : [];
                    dispatch(
                        setAddQuoteCompletedStep([ADD_QUOTE_KEYS.SHIPPER_DETAILS, ...middleStopStepKeys, ADD_QUOTE_KEYS.CONSIGNEE_DETAILS, ADD_QUOTE_KEYS.SHIPMENT_DETAILS, ADD_QUOTE_KEYS.DIMENSIONS])
                    );

                    // Every step is pre-filled from the source quote, so land the customer on the LAST
                    // step (review + submit) instead of making them click Next through each one. The
                    // last step depends on the stepper shape (classic FTL has no Dimensions step), so
                    // resolve it from ADD_QUOTE_STEPS rather than hardcoding a key.
                    const wizardSteps = ADD_QUOTE_STEPS(t, rest.classification, multiStop?.stopCount ?? 2, multiStop?.stops ?? {});
                    const lastStepKey = wizardSteps[wizardSteps.length - 1]?.key;

                    if (lastStepKey) {
                        dispatch(setAddQuoteStep(lastStepKey));
                    }

                    navigate(PATH.ADD_QUOTE);
                }

                setDuplicatingQuoteId(null);
            } catch (error: any) {
                if (error.code !== 'ERR_CANCELED') {
                    WalToast.error(error?.response?.data?.message ?? t('Common.Something_went_wrong'));
                }
                setDuplicatingQuoteId(null);
            }
        },
        [currentUser, dispatch, navigate, t]
    );

    return { duplicateQuote, duplicatingQuoteId };
};

export default useDuplicateQuote;
